import {useCallback, useState} from 'react';

import {SpecialtiesRefund} from 'domain/usecases';
import {useDebounce} from 'presentation/hooks';
import {useGetRefundSpecialtiesMutation} from '../mutations/useGetRefundSpecialtiesMutation';

export function useGetSpecialties() {
	const [open, setOpen] = useState(false);
	const [fetchError, setFetchError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [options, setOptions] = useState<SpecialtiesRefund[]>([]);
	const [, setPage] = useState(1);
	const [search, setSearch] = useState('');
	const [hasMore, setHasMore] = useState(true);

	const LIMIT_PER_PAGE = 100;

	const {mutateAsync: getRefundSpecialties, reset} = useGetRefundSpecialtiesMutation();

	const fetchList = useCallback(
		async ({searchTerm, pageValue}: {searchTerm?: string; pageValue: number}) => {
			try {
				setLoading(true);
				const newSpecialties = await getRefundSpecialties({
					page: pageValue,
					search: searchTerm?.trim(),
					limit: LIMIT_PER_PAGE,
				});

				const totalItems = newSpecialties.content.total;

				if (pageValue === 1) {
					setOptions(newSpecialties.content.specialties);
				} else {
					setOptions(prev => [...prev, ...newSpecialties.content.specialties]);
				}

				const totalPages = Math.ceil(totalItems / LIMIT_PER_PAGE);

				setHasMore(pageValue < totalPages);
				setLoading(false);
			} catch (error) {
				setLoading(false);
				reset();
				setOpen(false);
				setFetchError(true);
			}
		},
		[search],
	);

	const [debouncedFetchList] = useDebounce(
		({searchTerm, pageValue}: {searchTerm?: string; pageValue: number}) => {
			fetchList({searchTerm, pageValue});
		},
		250,
	);

	const handleSearchChange = useCallback(
		(newSearch: string) => {
			setSearch(newSearch);
			setPage(1);
			setOptions([]);
			debouncedFetchList({searchTerm: newSearch, pageValue: 1});
		},
		[fetchList],
	);

	const loadMore = () => {
		if (!loading && hasMore) {
			setPage(prev => {
				const newPage = prev + 1;

				fetchList({searchTerm: search, pageValue: newPage});

				return newPage;
			});
		}
	};

	const handleOpen = useCallback(async () => {
		setOpen(true);
		await fetchList({pageValue: 1});
	}, []);

	const handleClose = useCallback(async () => {
		setOpen(false);
		setSearch('');
		setPage(1);
		setOptions([]);
	}, []);

	const handleRefresh = useCallback(async () => {
		setFetchError(false);
	}, []);

	return {
		loadingList: loading,
		options,
		open,
		handleOpen,
		handleClose,
		fetchError,
		setFetchError,
		handleRefresh,
		loadMore,
		handleSearchChange,
	};
}
